import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  IconButton,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';
import {
  AccountCircle as AccountCircleIcon,
  Close as CloseIcon
} from '@material-ui/icons';
import CardTitle from '../../../common/card/CardTitle';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import UserLink from '../../users/components/UserLink';
import { User } from '../../../user/User.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { useUpdateFile } from '../Document.hook';
import { BaseDocumentCardProps } from '../Document.interface';
import AssignDocumentOwnerDialog from './AssignDocumentOwnerDialog';
import { useUserContext } from '../../../user/User.context';

const DocumentOwnerCard: React.FC<BaseDocumentCardProps> = ({
  document,
  onChange,
  onUpdate,
  onError
}) => {
  const { user, hasRole } = useUserContext();
  const [assignOwner, setAssignOwner] = useState<User | null | undefined>(
    document.ownedBy
  );
  const [openAssignDialog, setOpenAssignDialog] = useState<boolean>(false);
  const [openRemoveDialog, setOpenRemoveDialog] = useState<boolean>(false);
  const [{ data: updatedFile, isLoading: isUpdating, error }, updateDocument] =
    useUpdateFile(document.id, document.isFolder);
  const { isFieldDisabled } = useTaskEntityFields(document.task);
  const hasAccess =
    user?.id === document.ownedBy?.id ||
    hasRole('Admin') ||
    hasRole('SiteAdmin') ||
    hasRole('DocumentAdmin');
  const disabledAssignOwner = isFieldDisabled('assignOwner') || !hasAccess;

  const userName: string = useMemo(() => {
    const { firstName = '', lastName = '' } = assignOwner || {};
    return `${firstName} ${lastName}`;
  }, [assignOwner]);

  useEffect(() => {
    setAssignOwner(document.ownedBy);
  }, [document.ownedBy]);

  useEffect(() => {
    if (updatedFile) {
      onChange?.(updatedFile);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedFile]);

  useEffect(() => {
    error && onError?.(error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <Card>
      {isUpdating && <LinearProgress />}
      <CardTitle>
        <AccountCircleIcon />
        Owner
      </CardTitle>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {assignOwner ? (
              <TableRow
                hover={!disabledAssignOwner}
                onClick={() => {
                  !disabledAssignOwner && setOpenAssignDialog(true);
                }}
              >
                <TableCell>
                  <UserLink type='name' disableLink={true} user={assignOwner} />
                </TableCell>
                <TableCell padding='checkbox'>
                  <IconButton
                    size='small'
                    disabled={disabledAssignOwner}
                    onClick={event => {
                      event.stopPropagation();
                      setOpenRemoveDialog(true);
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow>
                <TableCell colSpan={2} align='center'>
                  <Link
                    component='button'
                    variant='body2'
                    disabled={disabledAssignOwner}
                    style={disabledAssignOwner ? { color: 'grey' } : {}}
                    onClick={() => setOpenAssignDialog(true)}
                  >
                    Assign
                  </Link>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {openAssignDialog && (
        <AssignDocumentOwnerDialog
          document={document}
          onCancel={() => setOpenAssignDialog(false)}
          onAssigned={document => {
            onChange?.(document);
            setOpenAssignDialog(false);
          }}
        />
      )}
      <ConfirmDialog
        title='Unassign User'
        confirmLabel='Confirm'
        open={openRemoveDialog && !!assignOwner}
        message={`Are you sure you want to unassign "${userName}" as Owner of this Document?`}
        onCancel={() => setOpenRemoveDialog(false)}
        onConfirm={() => {
          updateDocument({ ownedBy: null });
          setOpenRemoveDialog(false);
        }}
      />
    </Card>
  );
};

export default DocumentOwnerCard;
